import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { Modal, Pagination } from "react-bootstrap";
import {
  getPurchasePlansAction,
  createPurchasePlanAction,
  updatePurchasePlanAction,
} from "../../store/actions/PurchasePlan/PurchasePlanActions";

const PurchasePlan = () => {
  const dispatch = useDispatch();

  // Use correct reducer and loading state
  const { purchasePlan, loading } = useSelector((state) => state.purchasePlan);
  const purchasePlanRows = purchasePlan?.rows || [];
  const totalPages = purchasePlan?.pages || 1;
  const [formErrors, setFormErrors] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [addCard, setAddCard] = useState(false);

  const [addFormData, setAddFormData] = useState({
    name: "",
    description: "",
    benifits: "",
    price: "",
    currency: "INR",
    duration: "",
    durationUnit: "months",
    maxListings: "",
    maxFeaturedListings: "",
    supportIncluded: true,
    isActive: true,
    isFree: false,
    sortOrder: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [editpropertyTypeData, setEditpropertyTypeData] = useState(null);

  const handleAddFormChange = (input) => {
    if (input?.target) {
      const { name, value } = input.target;

      let updatedValue = value;

      // Convert values based on field name
      if (
        ["isEnabled", "supportIncluded", "isActive", "isFree"].includes(name)
      ) {
        updatedValue = value === "true";
      } else if (
        [
          "price",
          "duration",
          "maxListings",
          "maxFeaturedListings",
          "sortOrder",
        ].includes(name)
      ) {
        updatedValue = value === "" ? "" : Number(value);
      }

      setAddFormData((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));

      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleAddFormSubmit = async (event) => {
    event.preventDefault();
    const {
      name,
      description,
      benifits,
      price,
      currency,
      duration
    } = addFormData;

    const errors = {};

    if (!name.trim()) errors.name = "Name is required.";
    // if (!description.trim()) errors.description = "Description is required.";
    if (!benifits.trim()) errors.benifits = "Benefits are required.";
    if (price === "" || isNaN(price)) errors.price = "Valid price is required.";
    if (!duration) errors.duration = "Duration is required.";

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      name,
      description,
      benifits: benifits.split(",").map((item) => item.trim()),
      price,
      currency,
      duration
    };

    try {
      let response;
      if (editMode && editpropertyTypeData?._id) {
        response = await dispatch(
          updatePurchasePlanAction(editpropertyTypeData._id, payload)
        );
      } else {
        response = await dispatch(createPurchasePlanAction(payload));
      }

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: editMode
            ? "Plan updated successfully."
            : "Plan created successfully.",
        });
        resetForm();
        dispatch(getPurchasePlansAction(currentPage, pageSize));
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.msg || "Something went wrong",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.msg || "Unexpected error occurred",
      });
    }
  };

  const handleEdit = (data) => {
    setEditMode(true);
    setEditpropertyTypeData(data);
    setFormErrors({});

    setAddFormData({
      name: data.name || "",
      description: data.description || "",
      benifits: Array.isArray(data.benifits) ? data.benifits.join(", ") : "",
      price: data.price ?? "",
      currency: data.currency || "INR",
      duration: data.duration ?? "",
    });

    setAddCard(true);
  };

  const resetForm = () => {
    setAddCard(false);
    setEditMode(false);
    setEditpropertyTypeData(null);
    setAddFormData({
      name: "",
      description: "",
      benifits: "",
      price: "",
      currency: "INR",
      duration: ""
    });
  };

  useEffect(() => {
    dispatch(getPurchasePlansAction(currentPage, pageSize));
  }, [dispatch, currentPage, pageSize]);

  return (
    <>
      <div className="col-12">
        <Modal className="modal fade" show={addCard} onHide={resetForm}>
          <form onSubmit={handleAddFormSubmit}>
            <div className="modal-header">
              <h4 className="modal-title fs-20">
                {editMode ? "Edit Plan" : "Add Plan"}
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={resetForm}
              ></button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              <div className="add-contact-box">
                <div className="add-contact-content">
                  {/* Name */}
                  <div className="form-group mb-3">
                    <label>Name</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formErrors.name ? "is-invalid" : ""
                      }`}
                      name="name"
                      value={addFormData.name}
                      onChange={handleAddFormChange}
                      placeholder="Plan Name"
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback">{formErrors.name}</div>
                    )}
                  </div>

                  {/* Description (Comma Separated) */}
                  {/* Description */}
                  <div className="form-group mb-3">
                    <label>Description</label>
                    <textarea
                      className={`form-control ${
                        formErrors.description ? "is-invalid" : ""
                      }`}
                      name="description"
                      value={addFormData.description}
                      onChange={handleAddFormChange}
                      placeholder="Enter a detailed description"
                    ></textarea>
                    {formErrors.description && (
                      <div className="invalid-feedback">
                        {formErrors.description}
                      </div>
                    )}
                  </div>

                  {/* Benefits (comma separated) */}
                  <div className="form-group mb-3">
                    <label>Benefits (comma separated)</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formErrors.benifits ? "is-invalid" : ""
                      }`}
                      name="benifits"
                      value={addFormData.benifits}
                      onChange={handleAddFormChange}
                      placeholder="e.g., Priority support, Analytics dashboard"
                    />
                    {formErrors.benifits && (
                      <div className="invalid-feedback">
                        {formErrors.benifits}
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="form-group mb-3">
                    <label>Price (INR)</label>
                    <input
                      type="number"
                      className={`form-control ${
                        formErrors.price ? "is-invalid" : ""
                      }`}
                      name="price"
                      value={addFormData.price}
                      onChange={handleAddFormChange}
                      placeholder="e.g., 100"
                    />
                    {formErrors.price && (
                      <div className="invalid-feedback">{formErrors.price}</div>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="form-group mb-3">
                    <label>Duration (in days)</label>
                    <input
                      type="number"
                      className={`form-control ${
                        formErrors.duration ? "is-invalid" : ""
                      }`}
                      name="duration"
                      value={addFormData.duration}
                      onChange={handleAddFormChange}
                      placeholder="e.g., 3"
                    />
                    {formErrors.duration && (
                      <div className="invalid-feedback">
                        {formErrors.duration}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="submit" className="btn btn-primary">
                {editMode ? "Update" : "Add"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-danger"
              >
                Discard
              </button>
            </div>
          </form>
        </Modal>

        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
            <h4 className="card-title mb-0">Pricing</h4>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Duration (in days)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    // Skeleton loader rows
                    Array.from({ length: 5 }).map((_, idx) => (
                      <tr key={idx}>
                        <td><span className="skeleton-box" style={{ width: '80px', height: '20px', display: 'inline-block' }} /></td>
                        <td><span className="skeleton-box" style={{ width: '60px', height: '20px', display: 'inline-block' }} /></td>
                        <td><span className="skeleton-box" style={{ width: '100px', height: '20px', display: 'inline-block' }} /></td>
                      </tr>
                    ))
                  ) : purchasePlanRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">No purchase plans found</td>
                    </tr>
                  ) : (
                    purchasePlanRows.map((item) => (
                      <tr key={item._id}>
                        <td>{item.name}</td>
                        <td >
                          {item.price}
                        </td>
                        <td >
                          {item.duration}
                        </td>
                        <td>
                          <div className="d-flex">
                            <button
                              className="btn btn-secondary shadow btn-xs sharp me-2"
                              onClick={() => handleEdit(item)}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-4">
              <div></div> {/* Empty div to align right side content */}
              <div className="d-flex align-items-center gap-3">
                <span className="text-muted">
                  Total Furnishing Status:{" "}
                  {purchasePlan?.total || purchasePlanRows.length}
                </span>
                <Pagination className="pagination-gutter pagination-circle pagination mb-0">
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  />
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Pagination.Item
                      key={i + 1}
                      active={i + 1 === currentPage}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  />
                </Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchasePlan;
